import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Briefcase, MapPin, Building2, ExternalLink, Clock, ArrowLeft, AlertCircle } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { ShareMenu } from '@/components/ShareMenu';
import { getJobBySlug } from '@/lib/api';

const SITE_URL = 'https://paisleyscotland.com';
const JOBS_FALLBACK_IMG = 'https://paisley.org.uk/jobs-placeholder.jpg';
const JOBS_FALLBACK_ALT = 'Jobs in Paisley, Renfrewshire & Glasgow';

const JobDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    getJobBySlug(slug)
      .then((res) => { if (!cancelled) setJob(res.data); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  const pageUrl = `${SITE_URL}/jobs/${slug}`;
  const pageTitle = job
    ? `${job.title} — ${job.employer} | ${job.location} | Paisley Scotland`
    : 'Job listing | Paisley Scotland';
  const pageDescription = job
    ? `${job.title} at ${job.employer} in ${job.location}. ${job.description ? job.description.slice(0, 140) : 'Apply now via Paisley Scotland.'}`
    : 'Latest job vacancies in Paisley and Renfrewshire.';

  // Map free-text job_type to Google's JobPosting employmentType enum
  const employmentType = (() => {
    const t = (job?.job_type || '').toLowerCase();
    if (t.includes('part')) return 'PART_TIME';
    if (t.includes('contract')) return 'CONTRACTOR';
    if (t.includes('temp')) return 'TEMPORARY';
    if (t.includes('intern')) return 'INTERN';
    if (t.includes('volunteer')) return 'VOLUNTEER';
    if (t.includes('apprentice')) return 'OTHER';
    return 'FULL_TIME';
  })();

  // Parse salary strings like "£30,000 - £35,000" or "£30k" into baseSalary
  const parseSalary = (s) => {
    if (!s) return null;
    const nums = s.replace(/,/g, '').match(/(\d+(?:\.\d+)?)(k)?/gi);
    if (!nums || !nums.length) return null;
    const toNum = (v) => {
      const m = v.toLowerCase().match(/(\d+(?:\.\d+)?)(k)?/);
      if (!m) return null;
      return parseFloat(m[1]) * (m[2] ? 1000 : 1);
    };
    const values = nums.map(toNum).filter(Boolean);
    if (!values.length) return null;
    return {
      '@type': 'MonetaryAmount',
      'currency': 'GBP',
      'value': {
        '@type': 'QuantitativeValue',
        ...(values.length === 1
          ? { 'value': values[0] }
          : { 'minValue': Math.min(...values), 'maxValue': Math.max(...values) }),
        'unitText': 'YEAR',
      },
    };
  };

  const isRemote = /remote|work from home|wfh/i.test(job?.location || '');
  const baseSalary = parseSalary(job?.salary);

  const jobPostingSchema = job ? {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    'title': job.title,
    'description': job.description || `${job.title} at ${job.employer}`,
    'datePosted': job.created_at || new Date().toISOString(),
    ...(job.expires_at ? { 'validThrough': job.expires_at } : {}),
    'employmentType': employmentType,
    'identifier': {
      '@type': 'PropertyValue',
      'name': job.employer || 'Paisley Scotland',
      'value': job.id || job.slug || slug,
    },
    'hiringOrganization': {
      '@type': 'Organization',
      'name': job.employer,
      ...(job.link ? { 'sameAs': job.link } : {}),
      ...(job.cover_image ? { 'logo': job.cover_image } : {}),
    },
    'jobLocation': {
      '@type': 'Place',
      'address': {
        '@type': 'PostalAddress',
        'addressLocality': job.location || 'Paisley',
        'addressRegion': 'Renfrewshire',
        'addressCountry': 'GB',
      },
    },
    ...(isRemote ? { 'jobLocationType': 'TELECOMMUTE' } : {}),
    'applicantLocationRequirements': {
      '@type': 'Country',
      'name': 'GB',
    },
    ...(baseSalary ? { 'baseSalary': baseSalary } : {}),
    ...(job.cover_image ? { 'image': job.cover_image } : {}),
    ...(job.link ? { 'url': job.link, 'directApply': true } : { 'url': pageUrl, 'directApply': false }),
  } : null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="job-detail-page">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:site_name" content="Paisley Scotland" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        {jobPostingSchema && (
          <script type="application/ld+json">
            {JSON.stringify(jobPostingSchema)}
          </script>
        )}
      </Helmet>

      <Navbar />

      <section className="pt-32 pb-12 bg-gradient-to-b from-indigo-600 to-indigo-700 text-white">
        <div className="max-w-4xl mx-auto px-4">
          <button
            onClick={() => navigate('/jobs')}
            className="inline-flex items-center gap-2 text-indigo-100 hover:text-white text-sm mb-6"
            data-testid="back-to-jobs"
          >
            <ArrowLeft className="h-4 w-4" /> All Paisley & Renfrewshire jobs
          </button>
          <Badge className="bg-white/20 text-white border-0 mb-4">
            <Briefcase className="h-3 w-3 mr-1" /> Job vacancy
          </Badge>
          {loading ? (
            <div className="h-12 w-2/3 bg-white/20 rounded animate-pulse" />
          ) : (
            <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-6">
              {job?.title || 'Job not found'}
            </h1>
          )}
          {!loading && job && (() => {
            const heroImage = job.cover_image || job.image_url || JOBS_FALLBACK_IMG;
            const isFallback = !job.cover_image && !job.image_url;
            return (
              <div className="rounded-xl overflow-hidden border border-white/10 shadow-2xl shadow-indigo-950/40 max-h-72" data-testid="job-detail-hero-image">
                <img
                  src={heroImage}
                  alt={isFallback ? JOBS_FALLBACK_ALT : `${job.title} — ${job.employer}`}
                  onError={(e) => {
                    if (e.target.src !== JOBS_FALLBACK_IMG) e.target.src = JOBS_FALLBACK_IMG;
                  }}
                  className="w-full h-full object-cover"
                />
              </div>
            );
          })()}
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4">
          {loading ? (
            <Card><CardContent className="p-8 text-center text-slate-500">Loading job...</CardContent></Card>
          ) : error || !job ? (
            <Card>
              <CardContent className="p-12 text-center">
                <AlertCircle className="h-10 w-10 text-slate-300 mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-slate-900 mb-2">Job not found</h2>
                <p className="text-slate-500 mb-6">This vacancy may have expired or been filled.</p>
                <Link to="/jobs">
                  <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="browse-jobs-btn">
                    Browse all jobs
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-8 space-y-6">
                <div className="flex flex-wrap gap-x-6 gap-y-2 text-slate-600 text-sm">
                  <span className="inline-flex items-center gap-1.5">
                    <Building2 className="h-4 w-4 text-indigo-500" /> {job.employer}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="h-4 w-4 text-indigo-500" /> {job.location}
                  </span>
                  {job.date_posted && (
                    <span className="inline-flex items-center gap-1.5">
                      <Clock className="h-4 w-4 text-indigo-500" /> Posted {job.date_posted}
                    </span>
                  )}
                </div>

                {job.description && (
                  <div className="prose prose-slate max-w-none">
                    <p className="text-slate-700 leading-relaxed whitespace-pre-line">{job.description}</p>
                  </div>
                )}

                <div className="flex flex-wrap gap-3 pt-4 border-t border-slate-100">
                  {job.link && (
                    <a href={job.link} target="_blank" rel="noopener noreferrer">
                      <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="apply-btn">
                        Apply for this job <ExternalLink className="h-4 w-4 ml-2" />
                      </Button>
                    </a>
                  )}
                  <ShareMenu
                    title={`${job.title} — ${job.employer}`}
                    description={`Now hiring in ${job.location}`}
                    url={pageUrl}
                    testId="share-job-detail"
                    variant="outline"
                  />
                </div>
                {job.source && job.link && (
                  <p className="text-xs text-slate-500 pt-3" data-testid="originally-from">
                    Originally from{' '}
                    <a
                      href={job.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sky-600 hover:underline font-medium"
                    >
                      {job.source} →
                    </a>
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default JobDetailPage;
